/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';

import { ExportToExcel } from 'library';

import { AuthContext } from 'authentication/AuthContext';
import { useUserRolePermissions } from 'hooks';
import { hasAllRoles, userRoles } from 'utils/currentUserRoles';
import { Badge } from '@mui/material';

const Header = ({ filterCount, fetchCSVReport, exportParams, setIsFiltersOpen, onOpenCreate }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { view } = useParams();

  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);

  const hasAffiliateShipper = affiliates.some(affiliate => affiliate.shipper);

  const hasCreatePermissions = R.and(isProductShippingManage, hasAffiliateShipper);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClick = () => {
    const urlPrefix = R.cond([
      [R.equals('batch'), R.always('batches')],
      [R.equals('shipment'), R.always('shipments')],
      [R.equals('distribution'), R.always('distributions')],
    ])(view);

    fetchCSVReport(`get-${urlPrefix}-export`, exportParams)
      .then(({ data }) => new Blob([data], { type: 'text/csv' }))
      .then(blob => downloadBlob(blob, `${urlPrefix}_report.csv`))
      .catch(() => {
        // do nothing
      });
  };

  return (
    <Stack
      spacing={lgUp ? 3 : 0}
      sx={{
        backgroundColor: 'surface.light',
        position: { xs: 'fixed', lg: 'relative' },
        pt: { xs: 2, lg: 0 },
        width: { xs: 'calc(100% - 32px)', lg: 'unset' },
        zIndex: 1,
      }}
    >
      <Stack direction={lgUp ? 'row' : 'column'} justifyContent="space-between">
        <Typography variant="h5">Batches Overview</Typography>
        <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', lg: 'flex' } }}>
          <IconButton
            aria-label="Export to Excel"
            onClick={() => handleClick()}
            size="large"
            sx={{
              background: 'white',
              borderRadius: 24,
              boxShadow: 1,
              padding: '6px',
              height: '32px',
              width: '32px',
            }}
            variant="text"
          >
            <ExportToExcel />
          </IconButton>
          {R.equals(view, 'batch') && (
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row-reverse',
              }}
            >
              {hasCreatePermissions && (
                <Button
                  onClick={onOpenCreate}
                  startIcon={<AddRoundedIcon sx={{ mt: '-2px' }} />}
                  sx={{ ml: '8px' }}
                >
                  New Batch
                </Button>
              )}
            </Stack>
          )}
          {R.equals(view, 'shipment') && (
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row-reverse',
              }}
            />
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          alignItems: 'center',
          display: { xs: 'flex', lg: 'block' },
          flexDirection: 'row',
          justifyContent: 'space-between',
          my: { xs: 2, lg: 0 },
        }}
      >
        <Button
          onClick={() => setIsFiltersOpen(true)}
          startIcon={<FilterListOutlinedIcon sx={{ color: 'primary.dark', mt: '-2px' }} />}
          sx={{
            borderColor: 'gray.main',
            borderRadius: 0.5,
            color: 'text.primary',
            height: 48,
            minWidth: { xs: 'inherit', md: '25%', lg: 180 },
            width: { xs: '80px', md: '25%', lg: 180 },
          }}
          variant="outlined"
        >
          <Typography display={{ xs: 'none', md: 'inherit' }}>Show Filters</Typography>
          {!!filterCount && (
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              badgeContent={filterCount}
              color="primary"
              sx={{
                width: '20px',
                height: '20px',
                marginRight: '-4px',
                marginBottom: '2px',
                marginLeft: '1rem',
                marginTop: '1.3rem',
              }}
            />
          )}
        </Button>
        {R.equals(view, 'batch') &&
          hasAllRoles([userRoles.PRODUCT_SHIPPING_MANAGE, userRoles.SHIPPER]) &&
          !lgUp && (
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row-reverse',
                width: { xs: '75%', lg: 153 },
              }}
            >
              <Button
                onClick={onOpenCreate}
                startIcon={<AddRoundedIcon sx={{ mt: '-2px' }} />}
                sx={{ ml: '8px' }}
              >
                New Batch
              </Button>
            </Stack>
          )}
      </Box>
    </Stack>
  );
};

Header.propTypes = {
  filterCount: PropTypes.number,
  exportParams: PropTypes.shape({}).isRequired,
  fetchCSVReport: PropTypes.func.isRequired,
  setIsFiltersOpen: PropTypes.func.isRequired,
  onOpenCreate: PropTypes.func.isRequired,
};

Header.defaultProps = {
  filterCount: null,
};

export default Header;
